import { useState, useEffect, useCallback } from 'react';
import { 
  isOnline, 
  isRunningAsPWA, 
  isInstallable, 
  listenForConnectivity,
  listenForInstallPrompt,
  installPWA,
  updateSW,
  getConnectionInfo
} from '@/lib/swRegister';

/**
 * Hook for offline/online status management
 */
export function useOffline() {
  // Safe initialization for Capacitor WebView
  const [online, setOnline] = useState(() => {
    try { return isOnline(); } catch { return true; }
  });
  const [isPWA, setIsPWA] = useState(() => {
    try { return isRunningAsPWA(); } catch { return false; }
  });
  const [installable, setInstallable] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(() => {
    try { return getConnectionInfo(); } catch { 
      return { effectiveType: 'unknown', downlink: 0, rtt: 0, saveData: false };
    }
  });

  useEffect(() => {
    // Skip all PWA/SW logic in Capacitor native apps
    if (typeof window === 'undefined' || !(window as any).navigator) {
      return;
    }
    
    // Check if running in Capacitor native
    const isCapacitorNative = typeof (window as any).Capacitor !== 'undefined' && 
                              (window as any).Capacitor.isNativePlatform?.() === true;
    
    let cleanup = () => {};
    let cleanupInstall: (() => void) | undefined;

    try {
      // Listen for connectivity changes
      cleanup = listenForConnectivity(
        () => {
          setOnline(true);
          try { setConnectionInfo(getConnectionInfo()); } catch {}
        },
        () => {
          setOnline(false);
        }
      );

      // Only listen for PWA events if not in native app
      if (!isCapacitorNative) {
        // Listen for PWA install prompt
        cleanupInstall = listenForInstallPrompt();

        // Listen for installable event
        const handleInstallable = () => setInstallable(true);
        window.addEventListener('pwa-installable', handleInstallable);

        // Listen for SW update
        const handleUpdate = () => setUpdateAvailable(true);
        window.addEventListener('sw-update', handleUpdate);

        return () => {
          cleanup();
          cleanupInstall?.();
          window.removeEventListener('pwa-installable', handleInstallable);
          window.removeEventListener('sw-update', handleUpdate);
        };
      }
    } catch (e) {
      console.warn('[useOffline] Error setting up listeners:', e);
    }

    return () => {
      try { cleanup(); } catch {}
      try { cleanupInstall?.(); } catch {}
    };
  }, []);

  // Update connection info only when the network actually changes.
  // (Previously we polled every 5s, which forced the entire app tree to
  // re-render and made the browser tab show a permanent "loading" spinner.)
  useEffect(() => {
    if (!online) return;

    const isCapacitorNative = typeof window !== 'undefined' &&
                              typeof (window as any).Capacitor !== 'undefined' &&
                              (window as any).Capacitor.isNativePlatform?.() === true;
    if (isCapacitorNative) return;

    const conn = (navigator as any)?.connection;
    if (!conn || typeof conn.addEventListener !== 'function') return;

    const handleChange = () => {
      try {
        const next = getConnectionInfo();
        setConnectionInfo((prev) => {
          if (
            prev.effectiveType === next.effectiveType &&
            prev.downlink === next.downlink &&
            prev.rtt === next.rtt &&
            prev.saveData === next.saveData
          ) {
            return prev;
          }
          return next;
        });
      } catch { /* ignore */ }
    };

    conn.addEventListener('change', handleChange);
    return () => {
      try { conn.removeEventListener('change', handleChange); } catch {}
    };
  }, [online]);

  const install = useCallback(async () => {
    const success = await installPWA();
    if (success) {
      setInstallable(false);
      setIsPWA(true);
    }
    return success;
  }, []);

  const update = useCallback(async () => {
    await updateSW();
    setUpdateAvailable(false);
  }, []);

  return {
    online,
    isPWA,
    installable,
    updateAvailable,
    connectionInfo,
    install,
    update,
    isSlowConnection: connectionInfo.effectiveType === '2g' || connectionInfo.effectiveType === 'slow-2g'
  };
}

/**
 * Hook for queuing actions when offline
 */
export function useOfflineQueue<T>() {
  const [queue, setQueue] = useState<T[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const addToQueue = useCallback((item: T) => {
    setQueue(prev => [...prev, item]);
  }, []);

  const removeFromQueue = useCallback((index: number) => {
    setQueue(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  const processQueue = useCallback(async (
    processor: (item: T) => Promise<void>
  ) => {
    if (isProcessing || queue.length === 0) return;

    setIsProcessing(true);

    try {
      for (let i = 0; i < queue.length; i++) {
        await processor(queue[i]);
        removeFromQueue(i);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [queue, isProcessing]);

  return {
    queue,
    isProcessing,
    addToQueue,
    removeFromQueue,
    clearQueue,
    processQueue,
    queueLength: queue.length
  };
}
